import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Image, X, Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface VirtualBackgroundSelectorProps {
  videoStream: MediaStream | null;
  onBackgroundChange: (background: 'none' | 'blur' | string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function VirtualBackgroundSelector({
  videoStream,
  onBackgroundChange,
  open,
  onOpenChange
}: VirtualBackgroundSelectorProps) {
  const [selectedBackground, setSelectedBackground] = useState<'none' | 'blur' | string>('none');
  const [customBackgrounds, setCustomBackgrounds] = useState<string[]>([]);
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Use external control if provided, otherwise use internal state
  const dialogOpen = open !== undefined ? open : internalDialogOpen;
  const setDialogOpen = onOpenChange || setInternalDialogOpen;

  // Predefined backgrounds
  const presetBackgrounds = [
    { id: 'office', url: '/backgrounds/office.jpg', name: 'Office' },
    { id: 'home', url: '/backgrounds/home.jpg', name: 'Home Office' },
    { id: 'beach', url: '/backgrounds/beach.jpg', name: 'Beach' },
    { id: 'mountains', url: '/backgrounds/mountains.jpg', name: 'Mountains' },
    { id: 'city', url: '/backgrounds/city.jpg', name: 'City' },
    { id: 'space', url: '/backgrounds/space.jpg', name: 'Space' },
  ];

  const handleBackgroundSelect = (background: 'none' | 'blur' | string) => {
    setSelectedBackground(background);
    onBackgroundChange(background);
    
    toast({
      title: "Background updated",
      description: background === 'none' ? 'Removed background' : background === 'blur' ? 'Background blurred' : 'Custom background applied'
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file",
        description: "Please upload an image file",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    // Create object URL for the image
    const imageUrl = URL.createObjectURL(file);
    setCustomBackgrounds(prev => [...prev, imageUrl]);
    
    toast({
      title: "Background uploaded",
      description: "Your custom background is ready to use"
    });
  };

  const removeCustomBackground = (url: string) => {
    setCustomBackgrounds(prev => prev.filter(bg => bg !== url));
    URL.revokeObjectURL(url);
    
    if (selectedBackground === url) {
      handleBackgroundSelect('none');
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Image className="h-4 w-4 mr-2" />
          Virtual Background
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Virtual Backgrounds</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Default Options */}
          <div>
            <h3 className="text-sm font-medium mb-3">Effects</h3>
            <div className="grid grid-cols-3 gap-3">
              <Card 
                className={`cursor-pointer transition-all ${selectedBackground === 'none' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => handleBackgroundSelect('none')}
              >
                <CardContent className="p-4 text-center">
                  <div className="h-20 flex items-center justify-center bg-muted rounded">
                    <X className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-sm mt-2">None</p>
                </CardContent>
              </Card>

              <Card 
                className={`cursor-pointer transition-all ${selectedBackground === 'blur' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => handleBackgroundSelect('blur')}
              >
                <CardContent className="p-4 text-center">
                  <div className="h-20 flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5 rounded backdrop-blur-sm">
                    <span className="text-xs font-medium">Blur</span>
                  </div>
                  <p className="text-sm mt-2">Blur</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Preset Backgrounds */}
          <div>
            <h3 className="text-sm font-medium mb-3">Preset Backgrounds</h3>
            <div className="grid grid-cols-3 gap-3">
              {presetBackgrounds.map((bg) => (
                <Card 
                  key={bg.id}
                  className={`cursor-pointer transition-all ${selectedBackground === bg.url ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => handleBackgroundSelect(bg.url)}
                >
                  <CardContent className="p-4 text-center">
                    <div className="h-20 flex items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20 rounded">
                      <Image className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="text-sm mt-2">{bg.name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Custom Backgrounds */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">Custom Backgrounds</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
            
            {customBackgrounds.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No custom backgrounds yet</p>
                <p className="text-xs mt-1">Upload your own images</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {customBackgrounds.map((url, index) => (
                  <Card 
                    key={index}
                    className={`cursor-pointer transition-all relative group ${selectedBackground === url ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => handleBackgroundSelect(url)}
                  >
                    <CardContent className="p-4 text-center">
                      <div className="h-20 rounded overflow-hidden">
                        <img src={url} alt="Custom background" className="w-full h-full object-cover" />
                      </div>
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCustomBackground(url);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
            <p className="font-medium mb-2">Tips:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Use good lighting for best results</li>
              <li>Sit in front of a solid-colored background</li>
              <li>Custom images should be landscape orientation</li>
              <li>Maximum file size: 5MB</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
